/**
 * Pricing Rules types for Partner Dashboard
 *
 * Core types are re-exported from generated admin types.
 * Execution types define GraphQL request/response shapes.
 */

import type { RewareAdminTypes } from '@troverecommerce/shared-rest-api';

// Re-export core types from generated admin types
export type PricingRule = RewareAdminTypes.PricingDiscountCriteriaAdmin;
export type PricingRuleInput = RewareAdminTypes.PricingRuleInput;
export type PricingRuleUpdateInput = RewareAdminTypes.PricingRuleUpdateInput;
export type PricingRuleEdge = RewareAdminTypes.PricingRuleEdge;
export type PricingRulesConnection = RewareAdminTypes.PricingRuleConnection;
export type PageInfo = RewareAdminTypes.PricingRulePageInfo;
export type BatchCreateResult = RewareAdminTypes.BatchCreateResult;
export type BatchCreateError = RewareAdminTypes.BatchCreateError;
export type DeletePricingRuleResult = RewareAdminTypes.DeletePricingRuleResult;
export type PricingRulePositionResult = RewareAdminTypes.PricingRulePositionResult;

// Dashboard-specific types

export const PRICING_TYPES = ['yerdle_price', 'condition'] as const;
export type PricingType = (typeof PRICING_TYPES)[number];

export const PRICING_TYPE_LABELS: Record<PricingType, string> = {
  yerdle_price: 'Yerdle Price',
  condition: 'Condition Discount',
};

export const SOURCE_COLUMNS = [
  'price',
  'msrp_price',
  'last_known_retail_price',
  'last_known_outlet_price',
  'absolute_price',
] as const;

export interface PricingFilters {
  department?: string;
  category?: string;
  tag?: string;
  name?: string;
  merchantCategory?: string;
  upc?: string;
  condition?: string;
}

export interface StartPricingImportResult {
  pricingDiscountId: number;
}

// GraphQL execution types

export interface FetchPricingRulesExecVariables {
  partnerId: number;
  pricingType: string;
  first?: number;
  after?: string | null;
  filters?: PricingFilters;
}

export interface FetchPricingRulesExecResponse {
  partner: {
    pricingRules: PricingRulesConnection;
  };
}

export interface CreatePricingRuleExecVariables {
  partnerId: number;
  pricingType: string;
  rule: PricingRuleInput;
}

export interface CreatePricingRuleExecResponse {
  partner: {
    createPricingRule: PricingRule;
  };
}

export interface UpdatePricingRuleExecVariables {
  partnerId: number;
  ruleId: number;
  updates: PricingRuleUpdateInput;
}

export interface UpdatePricingRuleExecResponse {
  partner: {
    updatePricingRule: PricingRule;
  };
}

export interface DeletePricingRuleExecVariables {
  partnerId: number;
  ruleId: number;
}

export interface DeletePricingRuleExecResponse {
  partner: {
    deletePricingRule: DeletePricingRuleResult;
  };
}

export interface UpdatePricingRulePositionExecVariables {
  partnerId: number;
  ruleId: number;
  newPosition: number;
}

export interface UpdatePricingRulePositionExecResponse {
  partner: {
    updatePricingRulePosition: PricingRulePositionResult;
  };
}

export interface StartPricingImportExecVariables {
  partnerId: number;
  pricingType: string;
}

export interface StartPricingImportExecResponse {
  partner: {
    startPricingImport: StartPricingImportResult;
  };
}

export interface AddToPricingImportExecVariables {
  partnerId: number;
  pricingDiscountId: number;
  rules: PricingRuleInput[];
}

export interface AddToPricingImportExecResponse {
  partner: {
    addToPricingImport: BatchCreateResult;
  };
}
